import React from 'react';
import { motion } from 'framer-motion';
import { cards, motion as motionTokens } from '../../styles/design-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * UnifiedCard Component
 * Standard card component with variants and optional hover animations
 * 
 * @param {string} variant - Card variant: 'default' | 'large' | 'compact' | 'elevated' | 'interactive'
 * @param {boolean} hoverable - Enable hover lift animation
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Card content
 * @param {function} onClick - Click handler (makes card interactive)
 */
const UnifiedCard = ({ 
  variant = 'default',
  hoverable = false,
  className = '', 
  children,
  onClick = null,
  ...props 
}) => {
  const prefersReducedMotion = useReducedMotion();
  
  // Get base card class from design tokens
  const baseClass = cards?.[variant] || cards?.default;
  
  // Add cursor pointer if clickable
  const interactiveClass = onClick ? 'cursor-pointer' : '';
  
  // Combine all classes
  const combinedClass = `${baseClass} ${interactiveClass} ${className}`?.trim();
  
  // Determine if hover animation should be enabled
  const shouldAnimate = (hoverable || onClick) && !prefersReducedMotion;
  
  // Animation variants
  const hoverAnimation = shouldAnimate ? motionTokens?.hover?.liftSubtle : {};
  const tapAnimation = shouldAnimate && onClick ? motionTokens?.tap?.scaleSubtle : {};

  return (
    <motion.div 
      className={combinedClass}
      onClick={onClick}
      whileHover={hoverAnimation}
      whileTap={tapAnimation}
      transition={{ duration: motionTokens?.duration?.fast, ease: motionTokens?.easing?.default }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default UnifiedCard;